<template>
  <div>
    <div class="chart-filters-button d-flex justify-content-center align-items-center"
         :class="{'show': chart.showFilters}" @click="toggleFilters()">
      <font-awesome-icon :icon="chart.showFilters ? 'chevron-right' : 'chevron-left'"></font-awesome-icon>
    </div>
    <div class="chart-filters-tab py-3 d-flex flex-column align-items-center justify-content-between"
         :class="{'show': chart.showFilters}">
      <div>
        <div class="chart-filters-title mb-3">Data breakdown:</div>
        <div class="input-wrapper">
          <label>Metadata Type:</label>
          <el-select
              placeholder="Select values"
              v-model="chart.filters.metadataType" collapse-tags
          >
            <el-option
                v-for="value in metadataTypeValues"
                :key="value.value" :label="value.name" :value="value.value">
            </el-option>
          </el-select>
        </div>
        <div class="input-wrapper mt-2">
          <label>Metadata:</label>
          <el-select
              placeholder="Select values"
              v-model="chart.filters.metadata" collapse-tags
          >
            <el-option
                v-for="value in metadataValues"
                :key="value.field" :label="value.label" :value="value.field" :disabled="!chart.filters.metadataType">
            </el-option>
          </el-select>
        </div>
        <button class="search-button btn btn-block mt-3" @click="applyFilters" type="button" :disabled="!(chart.filters.metadata && chart.filters.metadataType)">
          Run
        </button>
      </div>
      <img src="../../../assets/images/icons/excel-export-H.svg" class="dwn-button" @click="download('performance')"/>
    </div>
    <div class="chart-filters-overlay" :class="{'show': chart.showFilters, hide: chart.hideOverlay}"
         @click="toggleFilters()"></div>
    <div v-if="rawData && rawData.length">
      <BarChart :chart-data="parsedChartData" :options="chart.options" :data-types="chartDataTypes"
                style="height: 300px; margin-bottom: 10px"></BarChart>
      <div class="table-container">
        <div class="d-flex table-header">
          <div class="column-header" :style="{width: `${100/rawData[0].metrics.length+1}%`}"
               @click="changeSort(rawData[0].category.name)">
            <span class="mr-1" style="text-transform: capitalize">{{parseCategory(rawData[0].category.name)}}</span>
            <div>
              <font-awesome-icon :icon="'caret-up'"
                                 :color="sortBy.key === rawData[0].category.name && sortBy.desc ? '#63c2de' : '#d1d4d7'"></font-awesome-icon>
              <font-awesome-icon :icon="'caret-down'"
                                 :color="sortBy.key === rawData[0].category.name && !sortBy.desc ? '#63c2de' : '#d1d4d7'"></font-awesome-icon>
            </div>
          </div>
          <div v-for="metric in rawData[0].metrics" class="column-header" :key="metric.name"
               :style="{width: `${100/rawData[0].metrics.length+1}%`, textTransform: 'capitalize'}"
               @click="changeSort(metric.name)">
            <span class="mr-1">{{parseMetric(metric.name)}}</span>
            <div>
              <font-awesome-icon :icon="'caret-up'"
                                 :color="sortBy.key === metric.name && sortBy.desc ? '#63c2de' : '#d1d4d7'"></font-awesome-icon>
              <font-awesome-icon :icon="'caret-down'"
                                 :color="sortBy.key === metric.name && !sortBy.desc ? '#63c2de' : '#d1d4d7'"></font-awesome-icon>
            </div>
          </div>
        </div>
        <div style="height: calc(100% - 50px)">
          <DynamicScroller
              :items="filteredData"
              :min-item-size="50"
              class="scroller"
              :emit-update="true"
          >
            <template v-slot="{ item, index, active }">
              <DynamicScrollerItem
                  :item="item"
                  :active="active"
                  :data-index="index"
              >
                <div class="data d-flex flex-column justify-content-center align-items-center">
                  <div class="main-data" style="padding: 0 10px;">
                    <div class="column-body" :style="{width: `${100/rawData[0].metrics.length+1}%`}">
                      {{ item.category.value }}
                    </div>
                    <div v-for="metric in rawData[0].metrics" class="column-body"
                         :key="`${metric.name}-${item.category.value}`"
                         :style="{width: `${100/rawData[0].metrics.length+1}%`}">
                      {{ parseValue(item.metrics.find(el => el.name === metric.name)) }}
                    </div>
                  </div>
                </div>
              </DynamicScrollerItem>
            </template>
          </DynamicScroller>
        </div>
      </div>
    </div>
    <div v-else-if="ready.performance" class="no-data d-flex justify-content-center align-items-center">
      <i v-if="searchExecuted">No data available for provided filters.</i>
      <i v-else>Select filters in order to see performance data.</i>
    </div>
  </div>
</template>

<script>
import 'vue-virtual-scroller/dist/vue-virtual-scroller.css'
import BarChart from '../../shared/BarChart'
import studyGetters from '../../../store/modules/studies/getterTypes'
import { mapActions, mapGetters } from 'vuex'
import studyActions from '../../../store/modules/studies/actionTypes'
import _ from 'lodash'

export default {
  name: 'FragrancePerformanceTable',
  components: { BarChart },
  props: {
    data: {
      type: Array,
      required: false
    },
    fragrances: {
      type: Array,
      default: null
    },
    filterFields: Object,
    ready: Object,
    searchData: Function,
    download: Function,
    parseSearchData: Function
  },
  data () {
    return {
      openedRows: [],
      searchExecuted: false,
      resizeToggle: false,
      sortBy: {
        key: 'client',
        desc: true
      },
      chart: {
        options: {
          scales: {
            xAxes: [{
              gridLines: {
                display: false
              },
              ticks: {
                callback: function (value) {
                  if (value.length > 18) {
                    return value.substr(0, 15) + '...'
                  } else {
                    return value
                  }
                }
              }
            }],
            yAxes: [{
              ticks: {
                min: 0
              }
            }]
          },
          tooltips: {
            enabled: true,
            mode: 'label',
            callbacks: {
              title: function (tooltipItems, data) {
                let idx = tooltipItems[0].index
                return data.labels[idx]
              }
            }
          },
          barValueSpacing: 20,
          responsive: true,
          maintainAspectRatio: false
        },
        barColors: ['#6c737e', '#7393a7', '#b5cfd8', '#e8ecf1'],
        showFilters: true,
        hideOverlay: false,
        filters: {
          metadata: '',
          metadataType: ''
        }
      }
    }
  },
  computed: {
    ...mapGetters({
      filters: studyGetters.GET_SEARCH_FILTERS,
      rawData: studyGetters.GET_ANALYTICS_PROFILE
    }),
    filteredData () {
      const arrayCopy = this.formatData()
      const isCategorySorted = arrayCopy[0].category.name === this.sortBy.key
      const sortedArray = arrayCopy.sort((a, b) => {
        if (isCategorySorted) {
          if (a.category.value < b.category.value) {
            return -1
          }
          if (a.category.value > b.category.value) {
            return 1
          }
          return 0
        } else {
          const valA = a.metrics.find(el => el.name === this.sortBy.key).value
          const valB = b.metrics.find(el => el.name === this.sortBy.key).value
          if (valA < valB) {
            return -1
          }
          if (valA > valB) {
            return 1
          }
          return 0
        }
      })
      return this.sortBy.desc ? sortedArray : sortedArray.reverse()
    },
    metadataValues () {
      return this.filterFields.aggregation_metadata && this.chart.filters.metadataType ? this.filterFields.aggregation_metadata.find(el => el.category === this.chart.filters.metadataType).options : []
    },
    metadataTypeValues () {
      return this.filterFields.aggregation_metadata ? this.filterFields.aggregation_metadata.map(el => {
        return { value: el.category, name: el.label }
      }) : []
    },
    parsedChartData () {
      const dataClone = _.cloneDeep(this.rawData)
      const metrics = [...new Set(dataClone[0].metrics.map(el => el.name))]
      const labels = dataClone.sort((a, b) => {
        const aValue = a.metrics.find(el => el.name === metrics[0]).value
        const bValue = b.metrics.find(el => el.name === metrics[0]).value
        return aValue > bValue ? -1 : aValue < bValue ? 1 : 0
      }).map(el => el.category.value)
      const data = []
      metrics.forEach((metric, index) => {
        const label = this.parseLabel(metric)
        const metricData = dataClone.map(el => el.metrics.find(value => value.name === metric))
        const dataArray = []
        metricData.forEach(el => {
          dataArray.push(el.value ? el.value : 0)
        })
        if (index === 0) {
          dataArray.sort((a, b) => a > b ? -1 : a < b ? 1 : 0)
        }
        data.push({
          label,
          backgroundColor: this.chart.barColors[index],
          data: dataArray
        })
      })
      return {
        labels,
        datasets: data
      }
    },
    chartDataTypes () {
      const dataTypes = {}
      const metrics = [...new Set(this.rawData[0].metrics.map(el => el.name))]
      metrics.forEach(metric => {
        const [label, aggregation] = metric.split('.')
        dataTypes[this.parseLabel(label)] = aggregation === 'mean' ? 'value' : 'percentage'
      })
      return dataTypes
    }
  },
  methods: {
    ...mapActions({
      downloadXlsx: studyActions.FETCH_XLSX_FILE
    }),
    formatData () {
      const formattedArray = []
      this.rawData.forEach((el, idx) => {
        formattedArray.push({
          id: idx,
          ...el
        })
      })
      return formattedArray
    },
    parseLabel (label) {
      const labelArray = label.split('.')
      return labelArray[0].charAt(0).toUpperCase() + labelArray[0].slice(1).replace(/_+/g, ' ')
    },
    parseMetricName (label) {
      const labelArray = label.split('.')
      return labelArray[1].replace(/_+/g, ' ')
    },
    parseMetric (value) {
      return `${this.parseLabel(value)}, ${this.parseMetricName(value)} [${value.split('.')[1] === 'mean' ? '5 points' : '%'}]`
    },
    parseValue (obj) {
      const value = obj.value ? obj.value : 0
      return `${obj.name.split('.')[1] === 'mean' ? value.toFixed(2) : Math.round(value)}${obj.name.split('.')[1] === 'mean' ? '' : '%'}`
    },
    parseCategory (value) {
      const data = value.split('.')
      return data.length > 1 ? `${data[0].replace(/_+/g, ' ')}, ${data[1].replace(/_+/g, ' ')}` : value.replace(/_+/g, ' ')
    },
    toggleFilters () {
      if (!this.chart.hideOverlay) {
        setTimeout(() => this.chart.hideOverlay = true, 200)
      } else {
        this.chart.hideOverlay = false
      }
      setTimeout(() => this.chart.showFilters = !this.chart.showFilters, 0)
    },
    clearToggledRows () {
      this.openedRows = []
    },
    async applyFilters () {
      this.ready.performance = false
      this.searchData(this.parseSearchData(this.filters), {
        category: this.chart.filters.metadataType,
        field: this.chart.filters.metadata
      }, 'performance')
      this.searchExecuted = true
    },
    changeSort (newKey) {
      if (this.sortBy.key === newKey) {
        const test = !this.sortBy.desc
        this.$set(this.sortBy, 'desc', test)
      } else {
        this.$set(this.sortBy, 'key', newKey)
        this.$set(this.sortBy, 'desc', true)
      }
    }
  },
  watch: {
    'chart.filters.metadataType': {
      handler (newVal, oldVal) {
        if (newVal !== oldVal) {
          this.chart.filters.metadata = ''
        }
      }
    },
    rawData () {
      if (this.rawData && this.rawData.length) {
        this.changeSort(this.rawData[0].metrics[0].name)
      }
    }
  }
}
</script>

<style scoped lang="scss">
@import "../../../assets/scss/variables.scss";

.table-container {
  overflow: scroll;
  position: relative;
  max-height: calc(55vh - 340px);
  min-height: 205px;
}

.main-data {
  display: flex;
  justify-content: space-around;
  align-items: center;
  height: 50px;
  width: 100%;
  cursor: pointer;
}

.additional-data {
  display: flex;
  justify-content: space-around;
  align-items: center;
  overflow: hidden;
  width: 98%;
  cursor: pointer;
}

.data {
  border-bottom: 1px solid #d1d4d7;
  transition: 0.1s background-color linear;
}

.data:hover {
  background-color: #f6f6f6;
}

.scroller {
  height: 100%;
}

.column-header {
  cursor: pointer;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 12px 0;
  transition: 0.1s background-color linear;
}

.column-header:hover {
  background-color: #f6f6f6;
}

.column-body {
  display: flex;
  justify-content: center;
  align-items: center;
}

.no-data {
  max-height: calc(55vh - 4rem);
  min-height: 523px;
  font-size: 18px;
}

.table-header {
  z-index: 3;
  position: sticky;
  top: 0;
  background-color: white;
  border: 1px solid #d1d4d7;
}

.no-hover:hover {
  background-color: unset;
}

.chart-filters-tab {
  overflow: hidden;
  margin-top: -1rem;
  z-index: 5;
  position: absolute;
  right: 0;
  height: 100%;
  width: 0;
  background-color: white;
  border-left: 0;
  transition: width 0.2s ease-in, border-left 0.2s ease-in;
}

.chart-filters-tab.show {
  width: 280px;
  border-left: 1px solid #d1d4d7;
}

.chart-filters-overlay {
  z-index: 4;
  width: 100%;
  height: 100%;
  position: absolute;
  margin-top: -1rem;
  margin-left: -1rem;
  background-color: rgba(0, 0, 0, 0);
  transition: background-color 0.2s ease-in;
}

.chart-filters-overlay.show {
  background-color: rgba(0, 0, 0, 0.1);
}

.chart-filters-overlay.hide {
  display: none;
}

.chart-filters-button {
  cursor: pointer;
  border-left: 1px solid #d1d4d7;
  border-bottom: 1px solid #d1d4d7;
  border-top: 1px solid #d1d4d7;
  z-index: 5;
  position: absolute;
  background-color: white;
  right: 0;
  width: 40px;
  height: 40px;
  bottom: 15px;
  transition: right 0.2s ease-in;
}

.chart-filters-button.show {
  right: 280px;
}

.input-wrapper {
  display: flex;
  flex-direction: column;
  justify-content: center;
  width: 100%;
}

.input-wrapper input {
  width: 100%;
}

.input-wrapper .el-select {
  width: 100%;
}

.input-wrapper label {
  color: #202124;
  margin-bottom: 2px;
  margin-left: 2px;
  font-family: 'Source Sans Pro', sans-serif;
  font-size: 13px;
  line-height: 20px;
  font-weight: 600;
}

.chart-filters-title {
  font-size: 1.1rem;
  white-space: nowrap;
}

.search-button {
  width: 230px;
  height: 48px;
  background-color: #300296;
  font-family: 'Open Sans', sans-serif;
  font-size: 16px;
  font-weight: 800;
  line-height: 30px;
  letter-spacing: 0;
  border-radius: 4px;
  color: white;
  text-transform: uppercase;
  transition: background-color 0.2s linear;

  &:hover {
    color: white;
    background-color: #9256D9;
  }
}

.download-button {
  height: 48px;
  background-color: $celery-500;
  border-radius: 4px;
  font-family: 'Open Sans', sans-serif;
  font-weight: 900;
  font-size: 16px;
  line-height: 30px;
  letter-spacing: 0;
  text-transform: uppercase;
  color: white;

  transition: background-color 0.2s linear;

  &:hover {
    color: white;
    background-color: $celery-700;
  }
}
</style>
